import {
  useTeamQuery,
  useTeamsShortQuery,
  useTransferTeamMemberMutation,
} from '@common/cache';
import { User } from '@common/types';
import { SelectChangeEvent } from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';

interface IUseTransferMembersProps {
  id: string;
}

function not(a: User[], b: User[]) {
  return a.filter(value => !b.includes(value));
}

function intersection(a: User[], b: User[]) {
  return a.filter(value => b.includes(value));
}

interface IInitialValues {
  id: string;
  users: User[];
  name: string;
  color: string;
}

const initialValues: IInitialValues = {
  id: '',
  users: [],
  name: '',
  color: '',
};

export const useTransferMembers = ({ id }: IUseTransferMembersProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [checked, setChecked] = useState<User[]>([]);
  const [rightTeamId, setRightTeamId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const transferTeamMemberMutation = useTransferTeamMemberMutation();

  const { data: leftTeamData = initialValues, refetch: refetchLeftTeamData } =
    useTeamQuery({
      teamId: id,
    });

  const { data: rightTeamData = initialValues, refetch: refetchRightTeamData } =
    useTeamQuery({
      teamId: rightTeamId,
      enabled: false,
    });

  const { data: teamNames = [] } = useTeamsShortQuery({});

  const filteredTeamNames = teamNames.filter(team => team.id !== id);

  const leftChecked = intersection(checked, leftTeamData.users);
  const rightChecked = intersection(checked, rightTeamData.users);

  useEffect(() => {
    if (rightTeamId) {
      refetchRightTeamData();
    }
  }, [rightTeamId]);

  const handleSelectChange = (event: SelectChangeEvent) => {
    setRightTeamId(event.target.value as string);
  };

  const handleClose = () => {
    setIsOpen(false);
    setRightTeamId('');
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const handleToggle = (value: User) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items: User[]) =>
    intersection(checked, items).length;

  const handleCheckedRight = () => {
    submitHandler({
      team_id: id,
      user_ids: leftChecked.map(a => a.id) || [],
      team2_id: rightTeamId,
    });
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    submitHandler({
      team_id: rightTeamId,
      user_ids: rightChecked.map(a => a.id) || [],
      team2_id: id,
    });
    setChecked(not(checked, rightChecked));
  };

  const submitHandler = async (values: {
    team_id: string;
    user_ids: string[];
    team2_id: string;
  }) => {
    try {
      setLoading(true);
      await transferTeamMemberMutation.mutateAsync({
        team_id: values.team_id,
        user_ids: values.user_ids,
        team2_id: values.team2_id,
      });
      refetchLeftTeamData();
      refetchRightTeamData();
      if (values.user_ids.length === 1) {
        toast.success('You transferred a team member!');
      } else {
        toast.success(`You transferred ${values.user_ids.length} members!`);
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  return {
    isOpen,
    handleClose,
    handleOnClick,
    handleToggle,
    numberOfChecked,
    handleCheckedRight,
    handleCheckedLeft,
    rightChecked,
    leftChecked,
    checked,
    leftTeamData,
    rightTeamData,
    filteredTeamNames: filteredTeamNames.map(o => ({
      value: o.id,
      label: o.name,
    })),
    rightTeamId,
    handleSelectChange,
    loading,
  };
};
